import axiosInstance from "../helpers/axiosInstance";

/**
 * Get all problem reports with pagination (Admin only)
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Page size (default: 10)
 * @returns {Promise} API response
 */
export const getAllReports = async (page = 1, pageSize = 10) => {
  try {
    const response = await axiosInstance.get("/problem-reports/admin/reports", {
      params: {
        page,
        page_size: pageSize,
      },
    });
    return response.data;
  } catch (error) {
    // Re-throw the error with response data for better error handling
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to fetch reports");
    }
    throw error;
  }
};

/**
 * Get a specific problem report by ID (Admin only)
 * @param {string} reportId - Report ID
 * @returns {Promise} API response
 */
export const getReportById = async (reportId) => {
  try {
    const response = await axiosInstance.get(
      `/problem-reports/admin/reports/${reportId}`
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to fetch report");
    }
    throw error;
  }
};

/**
 * Accept a problem report (Admin only)
 * @param {string} reportId - Report ID
 * @returns {Promise} API response
 */
export const acceptReport = async (reportId) => {
  try {
    const response = await axiosInstance.post(
      `/problem-reports/admin/reports/${reportId}/accept`
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to accept report");
    }
    throw error;
  }
};

/**
 * Reject a problem report (Admin only)
 * @param {string} reportId - Report ID
 * @returns {Promise} API response
 */

export const rejectReport = async (reportId) => {
  try {
    const response = await axiosInstance.post(
      `/problem-reports/admin/reports/${reportId}/reject`
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to reject report");
    }
    throw error;
  }
};

/**
 * Send a custom response to a problem report (Admin only)
 * @param {string} reportId - Report ID
 * @param {string} customMessage - Custom message to send
 * @returns {Promise} API response
 */
export const sendCustomResponse = async (reportId, customMessage) => {
  try {
    const response = await axiosInstance.post(
      `/problem-reports/admin/reports/${reportId}/custom-response`,
      {
        custom_message: customMessage,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to send custom response");
    }
    throw error;
  }
};

/**
 * Mark a problem report as read (Admin only)
 * @param {string} reportId - Report ID
 * @returns {Promise} API response
 */
export const markReportAsRead = async (reportId) => {
  try {
    const response = await axiosInstance.post(
      `/problem-reports/admin/reports/${reportId}/mark-read`
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to mark report as read");
    }
    throw error;
  }
};

/**
 * Get notification counts for admin dashboard (Admin only)
 * @returns {Promise} API response
 */
export const getNotificationCounts = async () => {
  try {
    const response = await axiosInstance.get(
      "/problem-reports/admin/notifications"
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to fetch notification counts");
    }
    throw error;
  }
};

/**
 * Submit a new problem report (User)
 * @param {Object} reportData - Report data
 * @param {string} reportData.name - User's name
 * @param {string} reportData.subject - Problem report subject
 * @param {string} reportData.description - Problem report description
 * @returns {Promise} API response
 */
export const submitReport = async (reportData) => {
  try {
    const response = await axiosInstance.post(
      "/problem-reports/submit",
      reportData
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to submit problem report");
    }
    throw error;
  }
};

// Default export with all functions for backward compatibility
const ProblemReportsService = {
  getAllReports,
  getReportById,
  acceptReport,
  rejectReport,
  sendCustomResponse,
  markReportAsRead,
  getNotificationCounts,
  submitReport,
};

export default ProblemReportsService; 